// Bewegungs-Katalog v1 für das Flexibilitäts-Screening (Fitness/Wellness).
// Siehe gait/docs/FLEX_TEST_CATALOG.md für die lesbare Tabelle dieser Daten.
//
// WICHTIG (Regulatory-Leitplanke, siehe gait/docs/FLEX_WELLNESS_ABGRENZUNG.md):
// Dies ist KEIN Medizinprodukt. Winkel/Indices werden NUR intern zur
// Ampel-Klassifikation verwendet und NIE als klinischer Befund oder
// Gradangabe an die Nutzerin ausgegeben.
//
// Reuse: angleAt/angleFromVertical/signedAngleDeg/vec aus ../gait/geometry,
// Joints/torsoLength aus ../pose/landmarks. Keine neue Pose-Quelle —
// ../pose/poseEngine bleibt die einzige Quelle (siehe Spec §4 Punkt 4).
//
// NICHT wiederverwendet: ../gait/rom.ts — dessen computeSagittalRom ist an
// Gangzyklen/EventResult/Gehrichtung gekoppelt und passt nicht auf eine
// gehaltene Einzelbewegung. Hier reicht eine einfache Perzentil-basierte
// Min/Max-Extraktion über die rohen Frames (siehe ./pipeline.ts).

import { Joints, torsoLength, type PoseFrame, type Side } from "../pose/landmarks";
import { angleAt, angleFromVertical, signedAngleDeg, vec, clamp, type Vec } from "../gait/geometry";
import type { GaitView } from "../store/types";
import type { FlexRegion } from "./types";

export type MovementMetric = "max" | "excursion";

export interface MovementDef {
  key: string;
  label: string;
  region: FlexRegion;
  view: GaitView;
  /** Movements that share one capture clip carry the same captureGroup. */
  captureGroup: string;
  perSide: boolean;
  metric: MovementMetric;
  unit: "deg" | "ratio";
  /**
   * Out-of-plane / inherently noisy on a single RGB camera — confidence is
   * capped regardless of capture quality (technical honesty, spec §5).
   */
  confidenceCap?: "medium" | "low";
  /** Per-frame magnitude for one side (or undefined for non-side movements). */
  valueAt(frame: PoseFrame, side: Side | undefined, aspect: number): number;
  /** Ampel-Band cut points (ascending) — wellness heuristic, KEINE klinische Norm. */
  cuts: [number, number];
  setup: string;
  instruction: string;
}

function up(): Vec {
  return { x: 0, y: -1 };
}

function dist2D(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export const MOVEMENTS: MovementDef[] = [
  {
    key: "neck_lateral_flex",
    label: "Kopf-Seitneige",
    region: "hws",
    view: "frontal",
    captureGroup: "hws",
    perSide: true,
    metric: "max",
    unit: "deg",
    cuts: [20, 35],
    setup: "Frontal zur Kamera, Schultern entspannt.",
    instruction: "Kopf langsam abwechselnd zur linken und rechten Schulter neigen.",
    valueAt(frame, side) {
      const j = new Joints(frame);
      const v = vec(j.shoulderCenter(), j.nose());
      const angle = signedAngleDeg(up(), v); // + = Neigung Richtung Bild-rechts
      const signed = side === "R" ? angle : -angle;
      return clamp(signed, 0, 90);
    },
  },
  {
    key: "neck_rotation",
    label: "Kopf-Drehung",
    region: "hws",
    view: "frontal",
    captureGroup: "hws",
    perSide: true,
    metric: "max",
    unit: "ratio",
    confidenceCap: "medium",
    cuts: [0.06, 0.12],
    setup: "Frontal zur Kamera, Schultern entspannt.",
    instruction: "Kopf langsam abwechselnd nach links und rechts drehen (Blick über die Schulter).",
    valueAt(frame, side) {
      const j = new Joints(frame);
      const t = torsoLength(frame) || 1;
      const offset = (j.nose().x - j.shoulderCenter().x) / t;
      const signed = side === "R" ? offset : -offset;
      return clamp(signed, 0, 1);
    },
  },
  {
    key: "shoulder_abduction",
    label: "Schulter-Abduktion",
    region: "schulter_brust",
    view: "frontal",
    captureGroup: "schulter_brust",
    perSide: true,
    metric: "max",
    unit: "deg",
    cuts: [70, 130],
    setup: "Frontal zur Kamera, Arme locker am Körper.",
    instruction: "Beide Arme seitlich so hoch wie möglich heben.",
    valueAt(frame, side) {
      const j = new Joints(frame);
      const s = side as Side;
      return angleAt(j.hip(s), j.shoulder(s), j.elbow(s));
    },
  },
  {
    key: "chest_opening",
    label: "Brustkorb-Öffnung",
    region: "schulter_brust",
    view: "frontal",
    captureGroup: "schulter_brust",
    perSide: false,
    metric: "max",
    unit: "ratio",
    cuts: [1.8, 2.6],
    setup: "Frontal zur Kamera, Arme locker am Körper.",
    instruction: "Arme seitlich öffnen, als würdest du den Brustkorb weiten.",
    valueAt(frame) {
      const j = new Joints(frame);
      const t = torsoLength(frame) || 1;
      return dist2D(j.wrist("L"), j.wrist("R")) / t;
    },
  },
  {
    key: "trunk_side_bend",
    label: "Rumpf-Seitneige",
    region: "rumpf",
    view: "frontal",
    captureGroup: "rumpf_seitneige",
    perSide: true,
    metric: "max",
    unit: "deg",
    cuts: [15, 28],
    setup: "Frontal zur Kamera, Füße hüftbreit.",
    instruction: "Oberkörper abwechselnd zur linken und rechten Seite neigen, Hüfte ruhig halten.",
    valueAt(frame, side) {
      const j = new Joints(frame);
      const v = vec(j.pelvisCenter(), j.shoulderCenter());
      const angle = signedAngleDeg(up(), v);
      const signed = side === "R" ? angle : -angle;
      return clamp(signed, 0, 90);
    },
  },
  {
    key: "trunk_forward_flex",
    label: "Rumpf-Vorbeuge",
    region: "rumpf",
    view: "lateral",
    captureGroup: "vorbeuge",
    perSide: false,
    metric: "max",
    unit: "deg",
    cuts: [45, 70],
    setup: "Seitlich zur Kamera, Beine durchgestreckt.",
    instruction: "Langsam nach vorne beugen, so weit wie angenehm möglich.",
    valueAt(frame) {
      const j = new Joints(frame);
      const v = vec(j.pelvisCenter(), j.shoulderCenter());
      return angleFromVertical(v);
    },
  },
  {
    key: "hamstring_hip_flex",
    label: "Hüfte / Beinrückseite",
    region: "huefte_hamstring",
    view: "lateral",
    captureGroup: "vorbeuge",
    perSide: true,
    metric: "max",
    unit: "deg",
    cuts: [60, 100],
    setup: "Seitlich zur Kamera, Beine durchgestreckt.",
    instruction: "Langsam nach vorne beugen, so weit wie angenehm möglich.",
    valueAt(frame, side) {
      const j = new Joints(frame);
      const s = side as Side;
      return 180 - angleAt(j.shoulderCenter(), j.hip(s), j.knee(s));
    },
  },
  {
    key: "knee_flexion",
    label: "Knieflexion",
    region: "knie_sprunggelenk",
    view: "lateral",
    captureGroup: "knie_sprunggelenk",
    perSide: true,
    metric: "max",
    unit: "deg",
    cuts: [90, 130],
    setup: "Seitlich zur Kamera, sicheren Halt suchen.",
    instruction: "Langsam in die Hocke gehen / Ferse Richtung Gesäß führen, so weit wie angenehm möglich.",
    valueAt(frame, side) {
      const j = new Joints(frame);
      const s = side as Side;
      return 180 - angleAt(j.hip(s), j.knee(s), j.ankle(s));
    },
  },
  {
    key: "ankle_dorsiflexion",
    label: "Sprunggelenk-Dorsalflexion",
    region: "knie_sprunggelenk",
    view: "lateral",
    captureGroup: "knie_sprunggelenk",
    perSide: true,
    // Excursion, not an absolute angle: standing orientation (facing left vs.
    // right) doesn't fix the sign convention, so we measure the spread of
    // the shank/foot angle instead — robust and still self-vs-self.
    metric: "excursion",
    unit: "deg",
    cuts: [15, 28],
    setup: "Seitlich zur Kamera, sicheren Halt suchen.",
    instruction: "Zehen mehrfach Richtung Schienbein ziehen und wieder ablegen.",
    valueAt(frame, side) {
      const j = new Joints(frame);
      const s = side as Side;
      const shank = vec(j.knee(s), j.ankle(s));
      const foot = vec(j.ankle(s), j.footIndex(s));
      return signedAngleDeg(shank, foot);
    },
  },
];

export function movementsByCaptureGroup(group: string): MovementDef[] {
  return MOVEMENTS.filter((m) => m.captureGroup === group);
}

export interface CaptureGroupDef {
  key: string;
  label: string;
  view: GaitView;
}

/** One entry per guided recording step — drives FlexScreen's step list. */
export const CAPTURE_GROUPS: CaptureGroupDef[] = [
  { key: "hws", label: "Kopf / HWS", view: "frontal" },
  { key: "schulter_brust", label: "Schulter / Brust", view: "frontal" },
  { key: "rumpf_seitneige", label: "Rumpf — Seitneige", view: "frontal" },
  { key: "vorbeuge", label: "Rumpf-Vorbeuge / Hamstring", view: "lateral" },
  { key: "knie_sprunggelenk", label: "Knie / Sprunggelenk", view: "lateral" },
];
